import { useEffect, useState, type ReactNode, type ChangeEvent } from "react";
import type { Tenant } from "@/mocks/superadmin/tenants";
import {
  adminApi,
  explainAdminError,
  type EntitlementsResult,
  type TenantOverrideRow,
  type BillingSubscriptionRow,
  type AdminStaffRow,
} from "@/lib/superadmin";
import type { Tier } from "@/lib/access";

interface TenantDetailModalProps {
  tenant: Tenant;
  onClose: () => void;
  onImpersonate?: (tenant: Tenant) => void;
  /** Called after any edit so the parent list can refresh. */
  onChanged?: () => void;
}

/** Treat the "—" placeholder from the list mapping as empty. */
function clean(v: string | null | undefined): string {
  return !v || v === "—" ? "" : v;
}

export const planLabels: Record<string, string> = {
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
  Starter: "Starter",
  Professional: "Professional",
  Enterprise: "Enterprise",
};

export const statusConfig: Record<string, { label: string; classes: string }> = {
  active: { label: "Active", classes: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trial: { label: "Trial", classes: "bg-amber-50 text-amber-700 border-amber-200" },
  churned: { label: "Churned", classes: "bg-red-50 text-red-500 border-red-200" },
  suspended: { label: "Suspended", classes: "bg-slate-100 text-slate-600 border-slate-200" },
};

function formatRevenue(val: number): string {
  if (val === 0) return "—";
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
  return `₹${val.toLocaleString("en-IN")}`;
}

type TabKey = "overview" | "subscription" | "team" | "features";
const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: "overview", label: "Overview", icon: "ri-information-line" },
  { key: "subscription", label: "Subscription", icon: "ri-bank-card-line" },
  { key: "team", label: "Team", icon: "ri-team-line" },
  { key: "features", label: "Features", icon: "ri-toggle-line" },
];

/**
 * The full tenant-detail content (quick actions + all sections) without any
 * modal chrome — shared by the /superadmin/tenants/:id page and the legacy
 * modal wrapper below.
 */
export function TenantDetailBody({
  tenant,
  onImpersonate,
  onChanged,
}: {
  tenant: Tenant;
  onImpersonate?: (tenant: Tenant) => void;
  onChanged?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [tab, setTab] = useState<TabKey>("overview");
  const isSuspended = tenant.status === "suspended";

  const toggleSuspend = async () => {
    setBusy(true); setNote("");
    try {
      await adminApi.tenants.setStatus(tenant.id, isSuspended ? "active" : "suspended");
      onChanged?.();
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Action failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {/* Quick actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => void toggleSuspend()}
          disabled={busy}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md border cursor-pointer disabled:opacity-50 whitespace-nowrap ${
            isSuspended ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-red-200 text-red-600 hover:bg-red-50"
          }`}
        >
          <i className={`${isSuspended ? "ri-play-circle-line" : "ri-pause-circle-line"} mr-1`}></i>
          {isSuspended ? "Reactivate account" : "Suspend account"}
        </button>
        {onImpersonate && (
          <button
            onClick={() => onImpersonate(tenant)}
            className="px-3 py-1.5 text-xs font-semibold rounded-md border border-background-200 text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shield-user-line mr-1"></i>Login as owner
          </button>
        )}
        <a
          href={tenant.slug ? `${window.location.origin}/${tenant.slug}` : undefined}
          target="_blank" rel="noopener noreferrer"
          className="px-3 py-1.5 text-xs font-semibold rounded-md border border-background-200 text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-external-link-line mr-1"></i>Public page
        </a>
        {note && <span className="text-xs text-accent-600">{note}</span>}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-background-200 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer whitespace-nowrap ${
              tab === t.key
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className={`${t.icon} text-base`}></i>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab panels */}
      {tab === "overview" && (
        <div className="space-y-6">
          <BusinessDetails tenant={tenant} onChanged={onChanged} />
          <Section title="Usage & Performance">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Outlets" value={tenant.outlets.toString()} icon="ri-building-4-line" />
              <MetricCard label="Monthly Revenue" value={formatRevenue(tenant.monthlyRevenue)} icon="ri-money-rupee-circle-line" />
              <MetricCard label="Orders / Day" value={tenant.ordersPerDay.toLocaleString("en-IN")} icon="ri-shopping-cart-line" />
              <MetricCard label="Last Active" value={clean(tenant.lastActive) || "—"} icon="ri-time-line" />
            </div>
          </Section>
        </div>
      )}
      {tab === "subscription" && <SubscriptionEditor restaurantId={tenant.id} onChanged={onChanged} />}
      {tab === "team" && <TeamSection restaurantId={tenant.id} />}
      {tab === "features" && <FeatureOverrides restaurantId={tenant.id} />}
    </div>
  );
}

export default function TenantDetailModal({ tenant, onClose, onImpersonate, onChanged }: TenantDetailModalProps) {
  const stColor = tenant.status === "active" ? "bg-emerald-500"
    : tenant.status === "trial" ? "bg-amber-400"
    : tenant.status === "churned" ? "bg-red-400" : "bg-slate-400";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40" onClick={onClose}></div>

      <div className="relative bg-background-50 rounded-xl border border-background-200 w-full max-w-3xl max-h-[88vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-100 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
              <i className="ri-store-2-line text-lg text-primary-600"></i>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-heading font-bold text-foreground-900 truncate">{tenant.businessName}</h2>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full border shrink-0 ${statusConfig[tenant.status].classes}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${stColor}`}></span>
                  {statusConfig[tenant.status].label}
                </span>
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 shrink-0">
                  {tenant.plan ? (planLabels[tenant.plan] ?? tenant.plan) : "No plan"}
                </span>
              </div>
              <p className="text-[11px] text-foreground-500 truncate">{tenant.slug ? `/${tenant.slug}` : tenant.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="px-6 py-5">
          <TenantDetailBody tenant={tenant} onImpersonate={onImpersonate} onChanged={onChanged} />
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-100 px-6 py-4 flex items-center justify-end gap-3 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors whitespace-nowrap cursor-pointer"
          >
            Close
          </button>
          {onImpersonate && (
            <button
              onClick={() => onImpersonate(tenant)}
              className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shield-user-line"></i>
              Login As {tenant.ownerName.split(" ")[0]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* Subscription editor — change plan, status, trial end and renewal date, or
   extend the free trial. Writes the tenant's subscriptions row. */
const SUB_STATUSES = ["trialing", "active", "expired", "cancelled"] as const;
const SUB_PLANS: (Tier | "none")[] = ["none", "starter", "growth", "pro"];

function isoToDateInput(iso: string | null | undefined): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : "";
}
function dateInputToIso(d: string): string | null {
  return d ? new Date(`${d}T23:59:59`).toISOString() : null;
}
function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  return `${d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` +
    (days >= 0 ? ` (${days}d left)` : ` (${-days}d ago)`);
}

function SubscriptionEditor({ restaurantId, onChanged }: { restaurantId: string; onChanged?: () => void }) {
  const [sub, setSub] = useState<BillingSubscriptionRow | null>(null);
  const [status, setStatus] = useState<typeof SUB_STATUSES[number]>("trialing");
  const [plan, setPlan] = useState<Tier | "none">("none");
  const [trialEnds, setTrialEnds] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { subscription } = await adminApi.tenants.subscription(restaurantId);
      setSub(subscription);
      setStatus((subscription?.status as typeof SUB_STATUSES[number]) ?? "trialing");
      setPlan((subscription?.planTier as Tier) ?? "none");
      setTrialEnds(isoToDateInput(subscription?.trialEndsAt));
      setPeriodEnd(isoToDateInput(subscription?.currentPeriodEnd));
    } catch (e) {
      setError(explainAdminError(e instanceof Error ? e.message : "Could not load subscription."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [restaurantId]);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(""), 2500); };

  const save = async () => {
    setBusy(true); setError("");
    try {
      await adminApi.tenants.setSubscription(restaurantId, {
        status,
        planTier: plan === "none" ? null : plan,
        trialEndsAt: dateInputToIso(trialEnds),
        currentPeriodEnd: dateInputToIso(periodEnd),
      });
      flash("Subscription updated.");
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const extend = async (days: number) => {
    setBusy(true); setError("");
    try {
      const { trialEndsAt } = await adminApi.tenants.extendTrial(restaurantId, days);
      flash(`Trial extended to ${new Date(trialEndsAt).toLocaleDateString("en-IN")}.`);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not extend trial.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-background-100 pt-5">
      <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Subscription</h3>
      <p className="text-[11px] text-foreground-400 mb-4">
        Change the plan, status or renewal date, or extend the free trial. Trial &amp; active plans get full access; expired/cancelled blocks the account.
      </p>
      {error && (
        <div className="flex items-start gap-2 px-3 py-2.5 mb-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs">
          <i className="ri-error-warning-line shrink-0 mt-0.5"></i>
          <span className="break-words">{error}</span>
        </div>
      )}
      {msg && <p className="text-xs text-emerald-600 mb-3">{msg}</p>}
      {loading ? (
        <div className="py-6 text-center"><i className="ri-loader-4-line text-xl text-primary-500 animate-spin"></i></div>
      ) : (
        <>
          {sub && (
            <p className="text-[11px] text-foreground-500 mb-3">
              Current: <span className="font-semibold">{sub.status ?? "—"}</span>
              {sub.planTier ? ` · ${planLabels[sub.planTier] ?? sub.planTier}` : ""}
              {" · trial ends "}{fmtWhen(sub.trialEndsAt)}
              {sub.currentPeriodEnd ? ` · renews ${fmtWhen(sub.currentPeriodEnd)}` : ""}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-semibold text-foreground-500">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as typeof SUB_STATUSES[number])}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50 capitalize cursor-pointer">
                {SUB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-foreground-500">Plan</span>
              <select value={plan} onChange={(e) => setPlan(e.target.value as Tier | "none")}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50 cursor-pointer">
                {SUB_PLANS.map((p) => <option key={p} value={p}>{p === "none" ? "No plan (trial)" : (planLabels[p] ?? p)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-foreground-500">Trial ends</span>
              <input type="date" value={trialEnds} onChange={(e) => setTrialEnds(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50" />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-foreground-500">Plan renews</span>
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50" />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button onClick={() => void save()} disabled={busy}
              className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md cursor-pointer disabled:opacity-50 whitespace-nowrap">
              {busy ? "Saving…" : "Save subscription"}
            </button>
            <span className="text-[11px] text-foreground-400 ml-1">Extend trial:</span>
            {[7, 14, 30].map((d) => (
              <button key={d} onClick={() => void extend(d)} disabled={busy}
                className="px-3 py-1.5 text-xs font-medium border border-background-200 rounded-md hover:bg-background-100 cursor-pointer disabled:opacity-50 whitespace-nowrap">
                +{d}d
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* Team — the staff the tenant has added. Toggle active, reset PIN, or remove. */
function TeamSection({ restaurantId }: { restaurantId: string }) {
  const [rows, setRows] = useState<AdminStaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { rows } = await adminApi.tenants.staff(restaurantId);
      setRows(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load team.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [restaurantId]);

  const toggleActive = async (s: AdminStaffRow) => {
    setBusy(s.id); setError("");
    try {
      await adminApi.tenants.setStaffActive(restaurantId, s.id, !s.active);
      setRows((prev) => prev.map((r) => (r.id === s.id ? { ...r, active: !r.active } : r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed.");
    } finally { setBusy(null); }
  };

  const remove = async (s: AdminStaffRow) => {
    if (!window.confirm(`Remove ${s.name} from this restaurant's team?`)) return;
    setBusy(s.id); setError("");
    try {
      await adminApi.tenants.deleteStaff(restaurantId, s.id);
      setRows((prev) => prev.filter((r) => r.id !== s.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed.");
    } finally { setBusy(null); }
  };

  const resetPin = async (s: AdminStaffRow) => {
    const pin = window.prompt(`New 4-digit POS PIN for ${s.name}:`)?.trim();
    if (!pin) return;
    if (!/^\d{4}$/.test(pin)) { setError("PIN must be exactly 4 digits."); return; }
    setBusy(s.id); setError("");
    try {
      await adminApi.tenants.resetStaffPin(restaurantId, s.id, pin);
      setRows((prev) => prev.map((r) => (r.id === s.id ? { ...r, hasPin: true } : r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not reset PIN.");
    } finally { setBusy(null); }
  };

  return (
    <div className="border-t border-background-100 pt-5">
      <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">
        Team ({rows.length})
      </h3>
      <p className="text-[11px] text-foreground-400 mb-4">Staff the owner has added. Toggle access, reset a POS PIN, or remove a member.</p>
      {error && <p className="text-xs text-accent-600 mb-3">{error}</p>}
      {loading ? (
        <div className="py-6 text-center"><i className="ri-loader-4-line text-xl text-primary-500 animate-spin"></i></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-foreground-400 py-2">No staff added yet.</p>
      ) : (
        <div className="rounded-lg border border-background-200 divide-y divide-background-100">
          {rows.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground-800 truncate">{s.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-500 capitalize">{s.role}</span>
                  {!s.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">disabled</span>}
                  {!s.hasPin && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">no PIN</span>}
                </div>
                <p className="text-[11px] text-foreground-400 truncate">{s.phone ?? s.email ?? "—"}</p>
              </div>
              <button onClick={() => void resetPin(s)} disabled={busy === s.id}
                className="text-xs px-2 py-1 rounded border border-background-200 hover:bg-background-100 cursor-pointer disabled:opacity-50 whitespace-nowrap">
                Reset PIN
              </button>
              <button onClick={() => void toggleActive(s)} disabled={busy === s.id}
                className={`text-xs px-2 py-1 rounded border cursor-pointer disabled:opacity-50 whitespace-nowrap ${s.active ? "border-background-200 hover:bg-background-100 text-foreground-600" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                {s.active ? "Disable" : "Enable"}
              </button>
              <button onClick={() => void remove(s)} disabled={busy === s.id}
                className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 cursor-pointer disabled:opacity-50 whitespace-nowrap">
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Per-tenant feature overrides. Each feature can be left on Plan default, or
   forced ON / OFF for this one restaurant (writes tenant_feature_overrides). */
function FeatureOverrides({ restaurantId }: { restaurantId: string }) {
  const [features, setFeatures] = useState<EntitlementsResult["features"]>([]);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [ent, ov] = await Promise.all([
          adminApi.entitlements.get(),
          adminApi.entitlements.tenantOverrides(restaurantId),
        ]);
        if (!alive) return;
        setFeatures(ent.features);
        const map: Record<string, boolean> = {};
        for (const r of ov.rows as TenantOverrideRow[]) map[r.feature] = r.enabled;
        setOverrides(map);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "Could not load overrides.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [restaurantId]);

  const setOverride = async (feature: string, value: boolean | null) => {
    setBusy(feature);
    try {
      await adminApi.entitlements.setTenantOverride(restaurantId, feature, value);
      setOverrides((prev) => {
        const next = { ...prev };
        if (value === null) delete next[feature];
        else next[feature] = value;
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setBusy(null);
    }
  };

  const current = (feature: string): "default" | "on" | "off" => {
    if (!(feature in overrides)) return "default";
    return overrides[feature] ? "on" : "off";
  };

  return (
    <div className="border-t border-background-100 pt-5">
      <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">
        Feature Overrides
      </h3>
      <p className="text-[11px] text-foreground-400 mb-4">
        Force a feature on/off for this tenant regardless of their plan. &quot;Default&quot; follows Feature Control.
      </p>
      {error && <p className="text-xs text-accent-600 mb-3">{error}</p>}
      {loading ? (
        <div className="py-6 text-center"><i className="ri-loader-4-line text-xl text-primary-500 animate-spin"></i></div>
      ) : (
        <div className="space-y-1.5">
          {features.map((f) => {
            const sel = current(f.feature);
            return (
              <div key={f.feature} className="flex items-center justify-between gap-3 py-1.5">
                <span className="text-sm text-foreground-700">{f.label || f.feature}</span>
                <div className="inline-flex rounded-md border border-background-200 overflow-hidden text-xs">
                  {(["default", "on", "off"] as const).map((opt) => (
                    <button
                      key={opt}
                      disabled={busy === f.feature}
                      onClick={() => void setOverride(f.feature, opt === "default" ? null : opt === "on")}
                      className={`px-2.5 py-1 capitalize cursor-pointer transition-colors ${
                        sel === opt
                          ? opt === "on"
                            ? "bg-emerald-500 text-white"
                            : opt === "off"
                            ? "bg-accent-500 text-white"
                            : "bg-primary-500 text-white"
                          : "bg-background-50 text-foreground-500 hover:bg-background-100"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Consistent section wrapper for even alignment across the modal. */
function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="border-t border-background-100 pt-5">
      <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">{title}</h3>
      {hint && <p className="text-[11px] text-foreground-400 mb-4">{hint}</p>}
      {!hint && <div className="mb-4" />}
      {children}
    </div>
  );
}

/* Editable business + owner-contact details. */
function BusinessDetails({ tenant, onChanged }: { tenant: Tenant; onChanged?: () => void }) {
  const [form, setForm] = useState({
    businessName: clean(tenant.businessName),
    ownerName: clean(tenant.ownerName),
    email: clean(tenant.email),
    phone: clean(tenant.phone),
    address: clean(tenant.address),
    gstin: clean(tenant.gstin),
    fssai: clean(tenant.fssai),
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      await adminApi.tenants.update(tenant.id, {
        restaurant: {
          name: form.businessName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          gstin: form.gstin.trim(),
          fssai: form.fssai.trim(),
        },
        owner: {
          name: form.ownerName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
        },
      });
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2500);
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Business details" hint="Edit the outlet profile and owner contact. Login email/password is managed by the owner.">
      {error && (
        <div className="flex items-start gap-2 px-3 py-2.5 mb-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs">
          <i className="ri-error-warning-line shrink-0 mt-0.5"></i>
          <span className="break-words">{error}</span>
        </div>
      )}
      {msg && <p className="text-xs text-emerald-600 mb-3">{msg}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Business name" value={form.businessName} onChange={set("businessName")} />
        <Field label="Owner name" value={form.ownerName} onChange={set("ownerName")} />
        <Field label="Contact email" value={form.email} onChange={set("email")} type="email" />
        <Field label="Phone" value={form.phone} onChange={set("phone")} />
        <Field label="GSTIN" value={form.gstin} onChange={set("gstin")} />
        <Field label="FSSAI" value={form.fssai} onChange={set("fssai")} />
        <div className="sm:col-span-2">
          <Field label="Address" value={form.address} onChange={set("address")} />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={() => void save()} disabled={busy}
          className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md cursor-pointer disabled:opacity-50 whitespace-nowrap">
          {busy ? "Saving…" : "Save details"}
        </button>
      </div>
    </Section>
  );
}

function Field({ label, value, onChange, type = "text" }: {
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-foreground-500">{label}</span>
      <input type={type} value={value} onChange={onChange}
        className="mt-1 w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400" />
    </label>
  );
}

function MetricCard({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-md bg-primary-100 flex items-center justify-center">
          <i className={`${icon} text-xs text-primary-600`}></i>
        </span>
        <span className="text-[10px] text-foreground-500 uppercase tracking-wider font-semibold">
          {label}
        </span>
      </div>
      <p className="text-lg font-heading font-bold text-foreground-900">{value}</p>
    </div>
  );
}